import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  applyBrandWorkflowTags,
  resolveBrandWorkflowTags,
} from '../brandFiltering';
import { BrandWorkflowFilterConfig } from '../brandWorkflowFilterConfig';
import useProductMode from './useProductMode';
import { getBrandFilter, User } from 'src/services/apiInterfaces';
import { useGetBrandsQuery } from 'src/services/brand/brandApi';
import { getCurrentUser } from 'src/slices/session/sessionSlice';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { Brand } from 'src/types/brandTypes';

const INTENT_FILTERS_PARAM = 'intent_filters';

const getIntentFilters = (searchParams: URLSearchParams): string[] =>
  (searchParams.get(INTENT_FILTERS_PARAM) ?? '').split(',').filter(Boolean);

const getFilterKey = (filter: string): string => {
  const separatorIndex = filter.indexOf('.');
  return separatorIndex === -1 ? filter : filter.slice(0, separatorIndex);
};

const hasWorkflowTagFilter = (intentFilters: string[]): boolean =>
  intentFilters.some(filter => getFilterKey(filter) === 'workflow_tags');

const areBrandsHydrated = ({
  areBrandsUninitialized,
  brands,
  isBrandsError,
  selectedBrandId,
}: {
  areBrandsUninitialized: boolean;
  brands: Brand[] | undefined;
  isBrandsError: boolean;
  selectedBrandId: string | undefined;
}): boolean =>
  !selectedBrandId ||
  brands !== undefined ||
  (isBrandsError && !areBrandsUninitialized);

const hasInitializedSelectedBrand = ({
  initializedParam,
  searchParams,
  selectedBrandId,
}: {
  initializedParam: string;
  searchParams: URLSearchParams;
  selectedBrandId: string | undefined;
}): boolean =>
  !!selectedBrandId && searchParams.get(initializedParam) === selectedBrandId;

const shouldHydrateUser = ({
  currentUser,
  shouldHydrateCurrentUser,
}: {
  currentUser: {
    data: User | null;
    error: boolean;
    loading: boolean;
  };
  shouldHydrateCurrentUser: boolean;
}): boolean =>
  shouldHydrateCurrentUser &&
  !currentUser.data &&
  !currentUser.loading &&
  !currentUser.error;

const shouldSyncBrandWorkflowTagsToUrl = ({
  brandsHydrated,
  hasInitializedBrand,
  isActiveProductMode,
  selectedBrandId,
  userLoaded,
}: {
  brandsHydrated: boolean;
  hasInitializedBrand: boolean;
  isActiveProductMode: boolean;
  selectedBrandId: string | undefined;
  userLoaded: boolean;
}): boolean =>
  isActiveProductMode &&
  userLoaded &&
  brandsHydrated &&
  !!selectedBrandId &&
  !hasInitializedBrand;

const buildInitializedSearchParams = ({
  brandFilter,
  brands,
  hasExplicitWorkflowTags,
  initializedParam,
  intentFilters,
  searchParams,
  selectedBrandId,
}: {
  brandFilter: string[];
  brands: Brand[];
  hasExplicitWorkflowTags: boolean;
  initializedParam: string;
  intentFilters: string[];
  searchParams: URLSearchParams;
  selectedBrandId: string;
}): URLSearchParams => {
  const nextSearchParams = new URLSearchParams(searchParams);

  if (!hasExplicitWorkflowTags) {
    const nextIntentFilters = applyBrandWorkflowTags(
      intentFilters,
      brandFilter,
      brands,
    );

    if (nextIntentFilters.length > 0) {
      nextSearchParams.set(INTENT_FILTERS_PARAM, nextIntentFilters.join(','));
    }
  }

  // Distinguishes a deliberately cleared Workflow tags filter from a URL
  // that has not received the user's default yet.
  nextSearchParams.set(initializedParam, selectedBrandId);
  return nextSearchParams;
};

const isBrandWorkflowFilterReady = ({
  brandsHydrated,
  hasExplicitWorkflowTags,
  hasInitializedBrand,
  isActiveProductMode,
  resolvedWorkflowTags,
  selectedBrandId,
  userError,
  userLoaded,
}: {
  brandsHydrated: boolean;
  hasExplicitWorkflowTags: boolean;
  hasInitializedBrand: boolean;
  isActiveProductMode: boolean;
  resolvedWorkflowTags: string[];
  selectedBrandId: string | undefined;
  userError: boolean;
  userLoaded: boolean;
}): boolean => {
  if (!isActiveProductMode || userError) {
    return true;
  }

  if (!userLoaded || !brandsHydrated) {
    return false;
  }

  if (!selectedBrandId || hasInitializedBrand) {
    return true;
  }

  if (hasExplicitWorkflowTags) {
    return true;
  }

  // Tagless / missing brands never write intent_filters; ready once resolved.
  return resolvedWorkflowTags.length === 0;
};

export const useInitializeBrandWorkflowTags = (
  config: BrandWorkflowFilterConfig,
  shouldHydrateCurrentUser = false,
): boolean => {
  const [productMode] = useProductMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(state => state.session.currentUser);
  const brandFilter = getBrandFilter(
    currentUser.data,
    config.brandFilterProduct,
  );
  const selectedBrandId = brandFilter[0];
  const isActiveProductMode = productMode === config.productMode;
  const shouldLoadBrands =
    isActiveProductMode && currentUser.data !== null && !!selectedBrandId;

  const {
    data: brands,
    isError: isBrandsError,
    isUninitialized: areBrandsUninitialized,
  } = useGetBrandsQuery(
    { brandType: config.brandType },
    { skip: !shouldLoadBrands },
  );

  const brandsHydrated = areBrandsHydrated({
    areBrandsUninitialized,
    brands,
    isBrandsError,
    selectedBrandId,
  });
  const intentFilters = useMemo(
    () => getIntentFilters(searchParams),
    [searchParams],
  );
  const resolvedWorkflowTags = useMemo(
    () => resolveBrandWorkflowTags(brandFilter, brands ?? []),
    [brandFilter, brands],
  );
  const hasExplicitWorkflowTags = hasWorkflowTagFilter(intentFilters);
  const hasInitializedBrand = hasInitializedSelectedBrand({
    initializedParam: config.initializedParam,
    searchParams,
    selectedBrandId,
  });

  useEffect(() => {
    if (!shouldHydrateUser({ currentUser, shouldHydrateCurrentUser })) {
      return;
    }

    void dispatch(getCurrentUser());
  }, [currentUser, dispatch, shouldHydrateCurrentUser]);

  useEffect(() => {
    if (
      !shouldSyncBrandWorkflowTagsToUrl({
        brandsHydrated,
        hasInitializedBrand,
        isActiveProductMode,
        selectedBrandId,
        userLoaded: currentUser.data !== null,
      }) ||
      !selectedBrandId
    ) {
      return;
    }

    setSearchParams(
      buildInitializedSearchParams({
        brandFilter,
        brands: brands ?? [],
        hasExplicitWorkflowTags,
        initializedParam: config.initializedParam,
        intentFilters,
        searchParams,
        selectedBrandId,
      }),
      { replace: true },
    );
  }, [
    brandFilter,
    brands,
    brandsHydrated,
    config.initializedParam,
    currentUser.data,
    hasExplicitWorkflowTags,
    hasInitializedBrand,
    intentFilters,
    isActiveProductMode,
    searchParams,
    selectedBrandId,
    setSearchParams,
  ]);

  return isBrandWorkflowFilterReady({
    brandsHydrated,
    hasExplicitWorkflowTags,
    hasInitializedBrand,
    isActiveProductMode,
    resolvedWorkflowTags,
    selectedBrandId,
    userError: currentUser.error,
    userLoaded: currentUser.data !== null,
  });
};
